"use client"

/**
 * Native toast provider. Replaces sonner because sonner's
 * `toast.dismiss(id)` was unreliable when paired with custom render
 * functions — the loading spinner kept floating behind the success
 * toast on cancel/retry flows.
 *
 * Animation philosophy (per Disney's 12 principles applied to UI):
 *  - Enter: 280ms with spring overshoot (cubic-bezier(.34,1.56,.64,1))
 *  - Exit:  180ms slide-out to the right + fade (cubic-bezier(.4,0,1,1))
 *  - Icon arrives ~80ms after the container — "secondary action"
 *  - Hover pauses both the auto-dismiss timer AND the visual progress
 *    bar — a real pause, not just a visual freeze
 *
 * Architecture:
 *  - Single mounted <ToastProvider /> at the root of the app holds
 *    the toast list in React state.
 *  - The exported singleton `toastBus` is a tiny event emitter that
 *    callers (lib/toast.tsx) push onto. The provider subscribes on
 *    mount and updates state.
 *  - Dismiss is two-phase: first the toast transitions to `exiting`
 *    state (triggers exit anim), then is removed from state ~200ms
 *    later. This is the classic AnimatePresence pattern done by hand,
 *    without pulling in framer-motion.
 */

import { useEffect, useRef, useState, useCallback } from "react"
import { createPortal } from "react-dom"
import { AppToast } from "@/components/ui/app-toast"

// ─────────────────────────────────────────────────────────────────
// Types + bus
// ─────────────────────────────────────────────────────────────────

export type ToastType = "success" | "error" | "info" | "loading"

export interface ToastInput {
  id?: string
  type: ToastType
  title: string
  description?: string
  /** Auto-dismiss after N ms. `loading` defaults to a 30s safety cap. */
  duration?: number | null
}

export interface ToastInstance {
  id: string
  type: ToastType
  title: string
  description: string
  duration: number | null
  createdAt: number
  exiting?: boolean
}

type Listener = (action: BusAction) => void

type BusAction =
  | { kind: "show";    toast: ToastInput }
  | { kind: "dismiss"; id?: string }

class ToastBus {
  private listeners = new Set<Listener>()
  emit(action: BusAction) { this.listeners.forEach((l) => l(action)) }
  subscribe(l: Listener) {
    this.listeners.add(l)
    return () => { this.listeners.delete(l) }
  }
}

export const toastBus = new ToastBus()

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID()
  }
  return `t-${Date.now()}-${Math.floor(Math.random() * 1e6)}`
}

const DEFAULT_DURATION_MS = 4_500
const LOADING_SAFETY_CAP_MS = 30_000
const EXIT_ANIM_MS = 200

// ─────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────

export function ToastProvider() {
  const [toasts, setToasts] = useState<ToastInstance[]>([])
  const dismissTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const exitTimers    = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
  const [mounted, setMounted] = useState(false)

  useEffect(() => { setMounted(true) }, [])

  // Cleanup on unmount — clear every pending timer so we don't leak.
  useEffect(() => {
    return () => {
      dismissTimers.current.forEach((t) => clearTimeout(t))
      exitTimers.current.forEach((t)    => clearTimeout(t))
      dismissTimers.current.clear()
      exitTimers.current.clear()
    }
  }, [])

  /** Move the toast into `exiting` state, then drop it from the list
   *  after the exit animation finishes. Idempotent — calling twice is
   *  a no-op. */
  const dismiss = useCallback((id?: string) => {
    setToasts((prev) => {
      if (!id) {
        // Dismiss all — mark every one exiting and schedule removal.
        prev.forEach((t) => scheduleRemoval(t.id))
        return prev.map((t) => ({ ...t, exiting: true }))
      }
      const target = prev.find((t) => t.id === id)
      if (!target || target.exiting) return prev
      scheduleRemoval(id)
      return prev.map((t) => (t.id === id ? { ...t, exiting: true } : t))
    })

    function scheduleRemoval(id: string) {
      const dt = dismissTimers.current.get(id)
      if (dt) { clearTimeout(dt); dismissTimers.current.delete(id) }
      const et = exitTimers.current.get(id)
      if (et) { clearTimeout(et); exitTimers.current.delete(id) }
      const handle = setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id))
        exitTimers.current.delete(id)
      }, EXIT_ANIM_MS)
      exitTimers.current.set(id, handle)
    }
  }, [])

  const upsert = useCallback((input: ToastInput) => {
    const id = input.id ?? newId()
    const duration =
      input.duration === null
        ? null
        : input.duration ?? (input.type === "loading" ? LOADING_SAFETY_CAP_MS : DEFAULT_DURATION_MS)

    const instance: ToastInstance = {
      id,
      type: input.type,
      title: input.title,
      description: input.description ?? "",
      duration,
      createdAt: Date.now(),
    }

    setToasts((prev) => {
      const existing = prev.findIndex((t) => t.id === id)
      if (existing >= 0) {
        const next = [...prev]
        next[existing] = instance
        // Reset timers for the replaced toast
        const dt = dismissTimers.current.get(id)
        if (dt) { clearTimeout(dt); dismissTimers.current.delete(id) }
        const et = exitTimers.current.get(id)
        if (et) { clearTimeout(et); exitTimers.current.delete(id) }
        return next
      }
      return [...prev, instance]
    })

    if (duration !== null && duration > 0) {
      const handle = setTimeout(() => dismiss(id), duration)
      dismissTimers.current.set(id, handle)
    }
  }, [dismiss])

  /** Pause / resume the auto-dismiss timer on hover. We restart the
   *  timer with the remaining duration when the user leaves. */
  const pausedRemaining = useRef<Map<string, number>>(new Map())

  const onHoverStart = useCallback((id: string) => {
    const t = dismissTimers.current.get(id)
    if (!t) return
    const inst = toasts.find((x) => x.id === id)
    if (!inst || inst.duration === null) return
    const elapsed = Date.now() - inst.createdAt
    const remaining = Math.max(0, inst.duration - elapsed)
    clearTimeout(t)
    dismissTimers.current.delete(id)
    pausedRemaining.current.set(id, remaining)
  }, [toasts])

  const onHoverEnd = useCallback((id: string) => {
    const remaining = pausedRemaining.current.get(id)
    if (remaining === undefined) return
    pausedRemaining.current.delete(id)
    const handle = setTimeout(() => dismiss(id), remaining)
    dismissTimers.current.set(id, handle)
  }, [dismiss])

  // Subscribe to the bus once.
  useEffect(() => {
    return toastBus.subscribe((action) => {
      if (action.kind === "show") upsert(action.toast)
      else                        dismiss(action.id)
    })
  }, [upsert, dismiss])

  if (!mounted) return null

  return createPortal(
    <div
      aria-live="polite"
      aria-atomic="true"
      className="pointer-events-none fixed inset-x-0 top-20 z-[200] flex flex-col items-center gap-2.5 px-4"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className="pointer-events-auto w-full max-w-sm"
          data-exiting={t.exiting ? "true" : undefined}
          style={{
            animation: t.exiting
              ? "toast-exit 180ms cubic-bezier(.4,0,1,1) forwards"
              : "toast-enter 280ms cubic-bezier(.34,1.56,.64,1) both",
          }}
        >
          <AppToast
            title={t.title}
            description={t.description || undefined}
            type={t.type}
            duration={t.duration}
            onDismiss={() => dismiss(t.id)}
            onHoverStart={() => onHoverStart(t.id)}
            onHoverEnd={() => onHoverEnd(t.id)}
          />
        </div>
      ))}
      <style>{`
        @keyframes toast-enter {
          0%   { opacity: 0; transform: translateY(-12px) scale(0.96); }
          70%  { opacity: 1; transform: translateY(2px)   scale(1.01); }
          100% { opacity: 1; transform: translateY(0)     scale(1);    }
        }
        @keyframes toast-exit {
          0%   { opacity: 1; transform: translateX(0)     scale(1);    }
          100% { opacity: 0; transform: translateX(120%)  scale(0.95); }
        }
      `}</style>
    </div>,
    document.body,
  )
}
